
import {defineComponent, nextTick, onMounted, onUnmounted, ref, watchEffect, watch, computed} from "vue";
import {useStore} from "vuex";
import {FormActions, FormGetters} from './../Store/index';
import {
  callPaq,
  getLocationWithoutHash,
  isStepAvailable,
  navigateToStepWithRouterLink,
  scrollIntoStep,
  updateStoreValueAndFilter,
  useShowElementByPredefinedManufacturer,
  validation,
  triggerMapSearch,
  checkGoogleMapsConsent,
  trackUserEmailForValuation
} from './../Composables/form';
import {isEmpty, stringToBoolean} from './../Composables/functions';
import {fetchSelectionInteractive, getItemByKey, toggleLoadingAnimation} from './../Composables/api';
import './../Store/types';

import RadioComponent from "./Form/RadioComponent.vue";
import SelectComponent from "./Form/SelectComponent.vue";
import DateComponent from "./Form/DateComponent.vue";
import InputComponent from "./Form/InputComponent.vue";
import ModalComponent from "./Form/ModalComponent.vue";
import HsnTsnWarningComponent from "./Form/HsnTsnWarningComponent.vue";

import CarComponent from "./Partials/CarComponent.vue";
import MotorcycleComponent from "./Partials/MotorcycleComponent.vue";
import ResultComponent from "./Partials/ResultComponent.vue";

export default defineComponent({
    name: "FormComponent",

    computed: {
      FormGetters() {
        return FormGetters
      }
    },

    components: {
      ModalComponent,
      HsnTsnWarningComponent,
      ResultComponent,
      RadioComponent,
      SelectComponent,
      DateComponent,
      InputComponent,
      MotorcycleComponent,
      CarComponent
    },

    props: {},

    setup() {
      // Get the Vuex store instance
      const store = useStore();

      // Get various data from the store using getters
      const icons = store.getters[`form/${FormGetters.GET_ICONS}`],
          labels = store.getters[`form/${FormGetters.GET_LABELS}`],
          settings = store.getters[`form/${FormGetters.GET_SETTINGS}`],
          images = store.getters[`form/${FormGetters.GET_IMAGES}`],
          isStickyButtonsContainer = ref(false),
          buttonsContainerPosition = ref<number | null>(null),
          formElement = ref<HTMLElement | null>(null);

      let isCar = ref(''),
          useHsnTsn = ref(false),
          selectedCar = ref({}),
          showResultPage = ref(false),
          oldShowResultPage = ref(false),
          shortEvalForm = ref<HTMLFormElement | null>(null),
          emailRequiredValue = ref(false),
          isCarOld = ref(''),
          isFormValid = ref(false),
          defaultValues = ref({
            isCar: true,
            useHsnTsn: false,
            manufacturer: '',
            mileage: '',
            vehicle: '',
            firstRegistration: ''
          }),
          hasBeenMounted = ref(false);

      // Check if there is an 'hsnImage' in the 'images' object and if it's not empty
      if (images.hasOwnProperty('hsnImage') && images['hsnImage'] !== "") {
        // If an 'hsnImage' exists, add an image element to the 'hsnBody' in 'settings'
        settings.hsnBody += `<div class="mt-2"><img src="${images.hsnImage}" alt="HSN Image" class="img-fluid"></div>`;
      }

      // Function to update isStickyButtonsContainer based on scroll position
      const updateStickyStateForFormButtons = () => {
        if (!isStepAvailable(store, 5)) {
          isStickyButtonsContainer.value = false;
        } else if (buttonsContainerPosition.value !== null && formElement.value) {
          const formElementTop = formElement.value.getBoundingClientRect().top + window.scrollY + 100;

          // Check if the scroll position is not at the top of the formElement
          isStickyButtonsContainer.value =
              (window.scrollY + window.innerHeight) < buttonsContainerPosition.value &&
              (window.scrollY + window.innerHeight) > formElementTop;
        }
      };

      // Function to initialize the sticky buttons container
      const updateButtonsContainerPosition = () => {
        nextTick(() => {
          formElement.value = document.querySelector('.shorteval-form-component');

          if (formElement.value) {
            buttonsContainerPosition.value = formElement.value.getBoundingClientRect().bottom + window.scrollY;
          }
        });
      };

      watch(() => store.getters[`form/${FormGetters.GET_IS_VALID}`], (newIsValidValue) => {
       isFormValid.value = newIsValidValue;
      });

      watch(() => store.getters[`form/${FormGetters.GET_DATA}`].availableSteps.value, (newAvailableSteps) => {
        updateButtonsContainerPosition();

        if (
            !isEmpty(newAvailableSteps) &&
            Array.isArray(newAvailableSteps) && newAvailableSteps.length > 0 &&
            hasBeenMounted.value
        ) {
          scrollIntoStep(store, newAvailableSteps[newAvailableSteps.length - 1]);
        }
      });

      // Watch for changes in isCar
      watchEffect(() => {
        isCar.value = store.getters[`form/${FormGetters.GET_INPUTS}`].isCar.value;
        useHsnTsn.value = store.getters[`form/${FormGetters.GET_INPUTS}`].useHsnTsn.value;
        selectedCar.value = store.getters[`form/${FormGetters.GET_DATA}`].selectedCar.value;
        showResultPage.value = store.getters[`form/${FormGetters.GET_SHOW_RESULT_PAGE}`];
        emailRequiredValue.value = store.getters[`form/${FormGetters.GET_INPUTS}`].fieldEmail.checked;

        if (isCarOld.value !== isCar.value) {
          isCarOld.value = isCar.value;

          updateButtonsContainerPosition();
        }

        // ZIP
        checkGoogleMapsConsent().then((canUseMaps) => {
            if (canUseMaps) {
              const zip = store.getters[`form/${FormGetters.GET_INPUTS}`].fieldLocation;
              if (Object.keys(zip).length > 0 && zip.valid) {
                  triggerMapSearch(zip.value);
              }
           }
        });

        nextTick(() => {
          // Hide content elements
          if (!showResultPage.value) {
            updateButtonsContainerPosition();

            const contentElementsDiv = document.querySelector('.short-eval-contentelements');
            if (contentElementsDiv && !contentElementsDiv.classList.contains('hidden')) {
              contentElementsDiv.classList.add('hidden');

              // Scroll to the element
              if (shortEvalForm.value) {
                // Calculate the target scroll position with a 20-pixel offset
                const targetScrollPosition = shortEvalForm.value.getBoundingClientRect().top + window.scrollY - 80;

                // Smooth scroll to the target position
                window.scrollTo({
                  top: targetScrollPosition,
                  behavior: 'smooth',
                });
              }
            }

            toggleLoadingAnimation(false);
          }

          if (oldShowResultPage.value !== showResultPage.value) {
            oldShowResultPage.value = showResultPage.value;

            // matomo
            callPaq('setCustomUrl', '', '', '', getLocationWithoutHash());
            callPaq('trackPageView');
          }
        });
      })

      const goToResultPage = async () => {
        await store.dispatch(`form/${FormActions.SET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`, false);
        await validation(store);

        if (isFormValid.value) {
          await trackUserEmailForValuation(store);

          callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'Button Click', 'Fahrzeugwert jetzt anzeigen - Enabled');
          if (store.getters[`form/${FormGetters.GET_INPUTS}`].fieldEmail.value) {
              callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'resultPage', 'GFW show result page mit Email');
          } else {
              callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'resultPage', 'GFW show result page ohne Email');
          }
          await navigateToStepWithRouterLink(store, 2, 'result');
          await store.dispatch(`form/${FormActions.SET_SHOW_RESULT_PAGE}`, Object.keys(selectedCar.value).length > 0);
          showResultPage.value = store.getters[`form/${FormGetters.GET_SHOW_RESULT_PAGE}`];
        } else {
          callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'Button Click', 'Fahrzeugwert jetzt anzeigen - Disabled');
          await store.dispatch(`form/${FormActions.SET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`, true);
          // Find the first invalid and touched input
          const firstInvalidInput = document.querySelector('.errorElement');

          if (firstInvalidInput) {
            // Scroll to the first invalid input
            const topPosition = firstInvalidInput.getBoundingClientRect().top - 70;
            window.scrollTo({
              top: window.scrollY + topPosition,
              behavior: 'smooth'
            });
          }
        }
      }

      // Handler for checkbox changes
      const handleCheckboxChange = (event: Event) => {
        const target = event.target as HTMLInputElement;
        // Update the store with the new checkbox
        store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {
          field: 'fieldEmail',
          key: 'checked',
          value: target.checked,
        });
        validation(store);
      };

      // _paq
      callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter', 'allowedVehicleTypes');
      callPaq('setCustomUrl', '', '', '', getLocationWithoutHash());
      callPaq('trackPageView');

      // Get the element and calculate its position when the component is mounted
      onMounted(async () => {
        // Helper function to dispatch store updates
        const dispatchUpdate = async (field, key, value) => {
          await store.dispatch(`form/${FormActions.UPDATE_DATA}`, { field, key, value });
        };

        // Data from Session
        const session = sessionStorage.getItem('datUsedcarsform');
        let vehicleModel = '';

        if (session) {
          const sessionObject = JSON.parse(session);

          if (!isEmpty(sessionObject)) {
            // Update predefinedManufacturerValues.hasValue
            await dispatchUpdate('predefinedManufacturerValues', 'hasValue', true);

            // Update manufacturer if available
            const vehicleManufacturer = sessionObject['manufacturer'];
            if (vehicleManufacturer){
              defaultValues.value.manufacturer = vehicleManufacturer;
              callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter', 'manufacturer');
            }

            // Update manufacturerValue
            await dispatchUpdate('predefinedManufacturerValues', 'manufacturerValue', {
              brand: defaultValues.value.manufacturer.replace(/&nbsp;/g, ' '),
              vehicle: '',
              vehicleType: true,
            });

            const vehicleTypes = sessionObject['allowedVehicleTypes'];
            if (vehicleTypes) {
              const isCar = vehicleTypes !== '3';
              const allowedVehicleTypes = isCar ? ['1', '2'] : ['3'];

              defaultValues.value.isCar = isCar;
              defaultValues.value.useHsnTsn = false;

              await store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {field: 'isCar', key: 'value', value: isCar});
              await store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'allowedVehicleTypes', key: 'value', value: allowedVehicleTypes});
              await store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'filter', key: 'value', value: {}});

              // Update manufacturerValue with vehicleType
              const predefinedValues = store.getters[`form/${FormGetters.GET_DATA}`].predefinedManufacturerValues || {};
              const manufacturerValue = predefinedValues.manufacturerValue || {};
              await dispatchUpdate('predefinedManufacturerValues', 'manufacturerValue', {
                ...manufacturerValue,
                vehicleType: isCar,
              });

              await updateStoreValueAndFilter(
                  store,
                  {name: 'isCar', 'filterProperty' : ['', '']},
                  isCar,
                  true,
                  false,
                  true,
                  true,
                  true
              );
            }

            const drivenKm = sessionObject['drivenkm']
            if (drivenKm) {
              defaultValues.value.mileage = drivenKm.toString();

              await dispatchUpdate('predefinedManufacturerValues', 'mileage', defaultValues.value.mileage);

              await updateStoreValueAndFilter(
                  store,
                  {name: 'mileage', 'filterProperty' : ['valuate','mileage']},
                  drivenKm.toString(),
                  true,
                  false,
                  true,
                  true,
                  true
              );
            }

            // Handle first registration
            const year = sessionObject['registrationYear'];
            const month = sessionObject['registrationMonth'];

            if (month && year){
              const date = new Date(year, month - 1); // Months are 0-based
              const yyyy = date.getFullYear();
              const mm = String(date.getMonth() + 1).padStart(2, '0');

              defaultValues.value.firstRegistration = `${mm}/${yyyy}`;

              await dispatchUpdate('predefinedManufacturerValues', 'firstRegistration', defaultValues.value.firstRegistration);

              await updateStoreValueAndFilter(
                  store,
                  {name: 'firstRegistration', 'filterProperty' : ['selection', 'firstRegistration']},
                  defaultValues.value.firstRegistration,
                  true,
                  false,
                  true,
                  true,
                  true
              );
              callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter', 'firstRegistration');
            }
          }

          await store.dispatch(`form/${FormActions.SET_HAS_DEFAULT_VALUES}`, true);
        }

        // Predefined Data - from Typoscript
        if (!isEmpty(settings.predefinedManufacturer)) {
          // Set default values for predefinedManufacturerValues
          vehicleModel = settings.predefinedModel.replace(/&nbsp;/g, ' ') ?? '';
          defaultValues.value = {
            isCar: settings.predefinedVehicleType,
            useHsnTsn: false,
            manufacturer: settings.predefinedManufacturer.replace(/&nbsp;/g, ' '),
            vehicle: '',
            mileage: '',
            firstRegistration: ''
          };

          // Update predefinedManufacturerValues
          await dispatchUpdate('predefinedManufacturerValues', 'hasValue', true);
          await dispatchUpdate('predefinedManufacturerValues', 'manufacturerValue', {
            brand: defaultValues.value.manufacturer,
            vehicle: '',
            vehicleType: true,
          });

          if (Object.keys(settings._mtm).length > 0 && settings._mtm.fgwVariantManufacturer !== '') {
            const mtmValues = settings._mtm;

            for (let prop in mtmValues) {
              if (!mtmValues.hasOwnProperty(prop)) continue;

              let value = mtmValues[prop] || '';

              await store.dispatch(`form/${FormActions.UPDATE_FUNNEL_STEP}`, {
                key: 'properties',
                value: {
                  ...store.getters[`form/${FormGetters.GET_FUNNEL_STEP_EXTRA}`],
                  ...{prop: stringToBoolean(value)}
                },
              });
            }
          }

          await store.dispatch(`form/${FormActions.SET_HAS_DEFAULT_VALUES}`, true);
        }

        updateButtonsContainerPosition();

        // Attach the scroll event listener
        window.addEventListener('scroll', updateStickyStateForFormButtons);

        // Initial update
        updateStickyStateForFormButtons();

        setTimeout(async () => {
          // Fetch new data
          await fetchSelectionInteractive(store);

          if (!isEmpty(settings.predefinedManufacturer) && vehicleModel) {
            const item = getItemByKey(store, vehicleModel ?? '', 'k', null, 'mainTypeGroupFb');

            if (!isEmpty(item)) {
              defaultValues.value.vehicle = item.k;

              await updateStoreValueAndFilter(
                  store,
                  {name: 'fieldModel', 'filterProperty' : ['selection', 'mainTypeGroupFb']},
                  defaultValues.value.vehicle,
                  true,
                  false,
                  true,
                  true,
                  true
              );

              // Retrieve the current predefinedManufacturerValues from the store
              const predefinedValues = store.getters[`form/${FormGetters.GET_DATA}`].predefinedManufacturerValues || {};
              const manufacturerValue = predefinedValues.manufacturerValue || {};
              await store.dispatch(`form/${FormActions.UPDATE_DATA}`, {
                field: 'predefinedManufacturerValues',
                key: 'manufacturerValue',
                value: {
                  ...manufacturerValue, // Spread the existing properties of manufacturerValue
                  vehicle: defaultValues.value.vehicle, // Override the 'vehicle' property
                },
              });
            }
          }

          hasBeenMounted.value = true;
        }, 300)

          const formEl = shortEvalForm.value as HTMLFormElement;
          if (formEl) {
              // Completely override native submit()
              formEl.submit = () => {
                  console.warn('Native form.submit() blocked');
              };

              // Optional: block manual jQuery-triggered .submit()
              formEl.addEventListener('submit', (e) => {
                  e.preventDefault();
                  console.warn('Form submit event blocked');
              });
          }
      });

      // Use onUnmounted to remove event listener when component is unmounted
      onUnmounted(() => {
        window.removeEventListener('scroll', updateStickyStateForFormButtons);
      });

      const showHsnTsnInfoBox = computed(() => {
        return useShowElementByPredefinedManufacturer(store, 'manufacturer').value;
      });

      const showFirstRegistrationDateInfoBox = computed(() => {
        const predefinedValues = store.getters[`form/${FormGetters.GET_DATA}`].predefinedManufacturerValues || {};

        return !(predefinedValues && predefinedValues.hasValue && predefinedValues.firstRegistration !== '' && predefinedValues.mileage !== '');
      });

      // Return the data
      return {
        icons,
        labels,
        settings,
        isCar,
        useHsnTsn,
        images,
        selectedCar,
        store,
        showResultPage,
        shortEvalForm,
        emailRequiredValue,
        isStickyButtonsContainer,
        defaultValues,
        isFormValid,
        showHsnTsnInfoBox,
        showFirstRegistrationDateInfoBox,
        handleCheckboxChange,
        goToResultPage,
        isStepAvailable
      }
    }
  })
